import { api } from '@/api/http';
import { fetchBuilderProjects, type IBuilderAndroidProject } from '@/api/builder-projects';
import BuildProgressBanner from '@/components/build/build-progress-banner';
import IconPicker from '@/components/build/icon-picker';
import { getActiveBuild } from '@/entities/build.entity';
import { $lastBuildComplete } from '@/entities/build-live.entity';
import LoadingWrapper from '@/components/loading-wrapper';
import { PageScroll } from '@/layouts/page-layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { InputGroup, InputGroupInput } from '@/components/ui/input-group';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { $features } from '@/entities/features-entity';
import { $profile } from '@/entities/profile-entity';
import { useUnit } from 'effector-react';
import { ContainerIcon, InfoIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import MyBuildsSection from './my-builds-section';
import { useMyBuilds } from './use-my-builds';
import { useBuildSubscription } from '@/hooks/use-build-subscription';
import BuildLogsSection from '@/components/build/build-logs-section';

export default function BuilderPage() {
	const features = useUnit($features);
	const profile = useUnit($profile);
	const isAdmin = profile?.role === 'admin';
	const [fileIsUploading, setIsFileUploading] = useState(false);
	const [builderProjects, setBuilderProjects] = useState<IBuilderAndroidProject[]>([]);
	const [projectsLoading, setProjectsLoading] = useState(true);

	useEffect(() => {
		let cancelled = false;

		async function loadProjects() {
			try {
				const projects = await fetchBuilderProjects();
				if (!cancelled) setBuilderProjects(projects);
			} finally {
				if (!cancelled) setProjectsLoading(false);
			}
		}

		void loadProjects();

		return () => {
			cancelled = true;
		};
	}, []);

	const defaultProjectDir = builderProjects[0]?.dir ?? 'OmadShou';

	const [isLoading, setIsLoading] = useState(false);
	const { builds, loading: buildsLoading, loadBuilds } = useMyBuilds();
	const activeBuild = getActiveBuild(builds);
	const { progress: liveProgress, logs } = useBuildSubscription(activeBuild?.id);
	const lastBuildComplete = useUnit($lastBuildComplete);
	const recentFailedBuild =
		lastBuildComplete && !lastBuildComplete.success
			? builds.find(build => build.id === lastBuildComplete.buildId)
			: null;
	const formLocked = Boolean(activeBuild);
	const createBuildLocked = formLocked || !features.builderEnabled;

	const [build, setBuild] = useState<{
		buildName: string | null;
		appIcon: string | null;
		appName: string | null;
		url?: string | null;
		hideToGp: boolean;
		hideIcon: boolean;
		androidProjectDir: string;
	}>({
		appIcon: null,
		appName: null,
		buildName: null,
		hideToGp: false,
		hideIcon: true,
		url: null,
		androidProjectDir: 'OmadShou',
	});

	async function handleSubmit() {
		if (!features.builderEnabled) {
			return toast.error('Билдер временно отключён администратором', { duration: 5000 });
		}

		if (formLocked) {
			return toast.error('Сначала дождитесь завершения текущего билда или отмените его', { duration: 5000 });
		}

		if (!build.appIcon) return toast.error('Загрузите иконку приложения', { duration: 5000 });

		if (!build.hideIcon && !build.hideToGp) return toast.error('Нельзя выключить hideIcon и hideToGp, что-то должно быть включено', { duration: 5000 });

		if (build.hideIcon === build.hideToGp) {
			return toast.error('hideIcon и hideToGp не могут быть одинаковыми', { duration: 5000 });
		}

		setIsLoading(true);
		api
			.post('/start-build', build)
			.then(async () => {
				toast.success('Билд успешно поставлен в очередь. АПК придет в телеграм бота', { duration: 5000 });
				setBuild({
					appIcon: null,
					appName: null,
					buildName: null,
					hideToGp: false,
					hideIcon: true,
					url: null,
					androidProjectDir: defaultProjectDir,
				});
				await loadBuilds();
			})
			.finally(() => {
				setIsLoading(false);
			});
	}

	async function handleBuildCancelled() {
		await loadBuilds();
	}

	return (
		<PageScroll contentClassName='p-2 flex flex-col gap-3'>
			<Card className='gap-0 py-0 shadow-sm overflow-hidden'>
				<CardHeader className='border-b px-4 py-4 flex'>
					<div className='flex items-center gap-3'>
						<div className='rounded-lg bg-chart-3/15 p-2'>
							<ContainerIcon className='text-chart-3' size={20} />
						</div>
						<div>
							<CardTitle className='text-chart-3'>Сборка APK</CardTitle>
							<CardDescription>Заполните параметры и поставьте билд в очередь</CardDescription>
						</div>
					</div>
				</CardHeader>

				<LoadingWrapper show={isLoading || fileIsUploading || projectsLoading}>
					<CardContent className='px-4 py-4 flex flex-col gap-4'>
						<BuildProgressBanner
							build={activeBuild}
							liveProgress={liveProgress}
							logs={logs}
							showAdminLogs={isAdmin}
							onCancel={() => void handleBuildCancelled()}
						/>

						{!activeBuild && recentFailedBuild?.hasBuildLog ? (
							<div className='rounded-xl border border-destructive/30 bg-destructive/10 p-4 flex flex-col gap-3'>
								<div>
									<p className='font-medium text-sm'>Сборка завершилась с ошибкой</p>
									<p className='text-xs text-muted-foreground'>{recentFailedBuild.appName || recentFailedBuild.id}</p>
								</div>
								<BuildLogsSection build={recentFailedBuild} />
							</div>
						) : null}

						{!features.builderEnabled && !activeBuild ? (
							<div className='rounded-xl border border-muted-foreground/30 bg-muted/40 px-4 py-3 flex items-center gap-3'>
								<div className='flex size-10 shrink-0 items-center justify-center rounded-full bg-muted/60'>
									<InfoIcon className='size-5 text-muted-foreground' />
								</div>
								<div className='min-w-0 flex-1'>
									<p className='font-medium text-sm'>Создание сборок временно отключено</p>
									<p className='text-xs text-muted-foreground'>Администратор отключил билдер. История сборок остаётся доступной.</p>
								</div>
							</div>
						) : null}

						<div className={createBuildLocked ? 'pointer-events-none opacity-60' : undefined}>
							<IconPicker
								value={build.appIcon}
								disabled={createBuildLocked}
								onChange={url => setBuild(p => ({ ...p, appIcon: url }))}
								onUploadingChange={setIsFileUploading}
							/>

							<Separator className='my-4' />

							<div className='flex flex-col gap-4'>
								<div className='flex flex-col gap-1.5'>
									<Label className='text-sm'>Проект</Label>
									<Select
										value={build.androidProjectDir}
										disabled={createBuildLocked}
										onValueChange={value => setBuild(p => ({ ...p, androidProjectDir: value }))}
									>
										<SelectTrigger className='w-full'>
											<SelectValue placeholder='Выберите проект' />
										</SelectTrigger>
										<SelectContent>
											{builderProjects.map(project => (
												<SelectItem key={project.id} value={project.dir}>
													{project.label}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>
								<div className='flex flex-col gap-1.5'>
									<Label className='text-sm'>Название билда</Label>
									<InputGroup>
										<InputGroupInput
											placeholder='my-build-01'
											disabled={createBuildLocked}
											onChange={e => setBuild(p => ({ ...p, buildName: e.target.value }))}
											value={build.buildName ?? ''}
										/>
									</InputGroup>
								</div>
								<div className='flex flex-col gap-1.5'>
									<Label className='text-sm'>Название приложения</Label>
									<InputGroup>
										<InputGroupInput
											placeholder='My App'
											disabled={createBuildLocked}
											onChange={e => setBuild(p => ({ ...p, appName: e.target.value }))}
											value={build.appName ?? ''}
										/>
									</InputGroup>
								</div>
								<div className='flex flex-col gap-1.5'>
									<Label className='text-sm'>WebView</Label>
									<InputGroup>
										<InputGroupInput
											placeholder='https://example.com'
											disabled={createBuildLocked}
											onChange={e => setBuild(p => ({ ...p, url: e.target.value }))}
											value={build.url ?? ''}
										/>
									</InputGroup>
									<p className='text-xs text-muted-foreground'>Необязательно</p>
								</div>
							</div>

							<Separator className='my-4' />

							<div className='flex flex-col gap-2'>
								<Label className='text-sm'>Метод скрытия</Label>
								<div className='grid grid-cols-2 gap-2'>
									<Button
										type='button'
										variant={build.hideIcon ? 'default' : 'outline'}
										className='h-auto py-2.5 text-xs whitespace-normal'
										disabled={createBuildLocked}
										onClick={() => setBuild(p => ({ ...p, hideIcon: true, hideToGp: false }))}
									>
										Скрыть иконку
									</Button>
									<Button
										type='button'
										variant={build.hideToGp ? 'default' : 'outline'}
										className='h-auto py-2.5 text-xs whitespace-normal'
										disabled={createBuildLocked}
										onClick={() => setBuild(p => ({ ...p, hideIcon: false, hideToGp: true }))}
									>
										Hide to GP
									</Button>
								</div>
								<p className='text-xs text-muted-foreground'>Выберите один метод скрытия приложения</p>
							</div>
						</div>
					</CardContent>

					<CardFooter className='border-t px-4 py-4'>
						<Button
							className='w-full'
							variant='default'
							disabled={
								createBuildLocked ||
								projectsLoading ||
								!build.buildName?.trim() ||
								!build.appName?.trim() ||
								!build.appIcon
							}
							onClick={handleSubmit}
						>
							{formLocked ? 'Билд выполняется' : !features.builderEnabled ? 'Сборка недоступна' : 'Начать билд'}
						</Button>
					</CardFooter>
				</LoadingWrapper>
			</Card>

			<MyBuildsSection builds={builds} loading={buildsLoading} onCancel={() => void handleBuildCancelled()} />
		</PageScroll>
	);
}
